import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socket';
import JoinRequestPanel from './JoinRequestPanel';
import '../styles/CompetitiveGame.css';

interface Room {
  id: number;
  roomCode: string;
  hostUserId: number;
  dictionaryName: string;
  dictionaryData: Record<string, string> | any; // Support all dictionary formats
  pairCount: number;
  maxPlayers: number;
  status: string;
  gameSeed: number;
  matching_mode?: 'one-to-one' | 'category' | 'sort';
}

interface OpponentProgress {
  userId: number;
  username: string;
  completed: number;
  total: number;
  attempts: number; // Track total attempts for real-time progress
  accuracy: number;
  finished: boolean;
  completionTime?: number;
  score?: number;
}

interface GameResult {
  user_id: number;
  username: string;
  completion_time: number;
  accuracy: number;
  score: number;
  placement: number;
  correct_matches: number;
  wrong_matches: number;
  total_attempts: number;
}

interface CumulativeResult {
  user_id: number;
  username: string;
  total_score: number;
  total_time: number;
  total_attempts: number;
  total_correct: number;
  total_wrong: number;
  rounds_played: number;
  cumulative_placement: number;
}

interface CompetitiveGameProps {
  room: Room;
  isHost: boolean;
  onGameEnd: (results: GameResult[]) => void;
  onLeave: () => void;
  onAnotherRound: () => void;
  // The actual game component render function
  renderGame: (
    gameData: Record<string, string> | any,
    pairCount: number,
    seed: number,
    onMatchAttempt: (correct: boolean) => void,
    onGameComplete: (stats: GameStats) => void,
    matchingMode?: 'one-to-one' | 'category' | 'sort'
  ) => React.ReactNode;
}

interface GameStats {
  completionTime: number;
  totalAttempts: number;
  correctMatches: number;
  wrongMatches: number;
}

const CompetitiveGame: React.FC<CompetitiveGameProps> = ({
  room,
  isHost,
  onGameEnd,
  onLeave,
  onAnotherRound,
  renderGame
}) => {
  const { user } = useAuth();
  const [opponents, setOpponents] = useState<Map<number, OpponentProgress>>(new Map());
  const [gameResults, setGameResults] = useState<GameResult[] | null>(null);
  const [cumulativeResults, setCumulativeResults] = useState<CumulativeResult[] | null>(null);
  const [roundNumber, setRoundNumber] = useState<number>(1);
  const [hasFinished, setHasFinished] = useState(false);
  const [quitNotification, setQuitNotification] = useState<string | null>(null);
  const [showScoreInfo, setShowScoreInfo] = useState(false);
  const [anotherRoundNotification, setAnotherRoundNotification] = useState<string | null>(null);
  const [usersInWaitingRoom, setUsersInWaitingRoom] = useState<Array<{ userId: number; username: string }>>([]);

  const handleOpponentProgress = useCallback((data: { userId: number; username?: string; correct: boolean }) => {
    if (data.userId === user?.id) return; // Ignore own updates

    setOpponents(prev => {
      const updated = new Map(prev);
      const opponent = updated.get(data.userId) || {
        userId: data.userId,
        username: data.username || 'Player', // Use username from event
        completed: 0,
        total: room.pairCount,
        attempts: 0,
        accuracy: 100,
        finished: false
      };

      // Update username if provided in event
      if (data.username && opponent.username === 'Player') {
        opponent.username = data.username;
      }

      // Increment attempts for ALL match attempts (both correct and wrong)
      opponent.attempts += 1;

      // Only increment completed pairs on correct matches
      if (data.correct) {
        opponent.completed += 1;
      }

      // Calculate real-time accuracy
      opponent.accuracy = opponent.attempts > 0
        ? (opponent.completed / opponent.attempts) * 100
        : 100;

      updated.set(data.userId, opponent);
      return updated;
    });
  }, [user, room.pairCount]);

  const handlePlayerCompleted = useCallback((data: {
    userId: number;
    username: string;
    completionTime: number;
    accuracy: number;
    score: number;
  }) => {
    setOpponents(prev => {
      const updated = new Map(prev);
      const existingOpponent = updated.get(data.userId);
      updated.set(data.userId, {
        userId: data.userId,
        username: data.username,
        completed: room.pairCount,
        total: room.pairCount,
        attempts: existingOpponent?.attempts || room.pairCount, // Use tracked attempts or default to pairCount
        accuracy: data.accuracy,
        finished: true,
        completionTime: data.completionTime,
        score: data.score
      });
      return updated;
    });
  }, [room.pairCount]);

  const handleGameEnded = useCallback((data: { currentRound: GameResult[]; cumulative: CumulativeResult[]; roundNumber: number }) => {
    setGameResults(data.currentRound);
    setCumulativeResults(data.cumulative);
    setRoundNumber(data.roundNumber);

    // Update stage to 'results' when game ends
    if (user) {
      socketService.getSocket()?.emit('update_user_stage', {
        roomId: room.id,
        userId: user.id,
        username: user.username,
        stage: 'results'
      });
    }
  }, [user, room.id]);

  const handlePlayerQuit = useCallback((data: { userId: number; username: string }) => {
    if (data.userId === user?.id) return; // Ignore own quit

    // Show notification
    setQuitNotification(`${data.username} has quit the game`);

    // Remove from opponents list
    setOpponents(prev => {
      const updated = new Map(prev);
      updated.delete(data.userId);
      return updated;
    });

    // Hide notification after 3 seconds
    setTimeout(() => {
      setQuitNotification(null);
    }, 3000);
  }, [user]);

  const handleAnotherRoundWaiting = useCallback((data: { userId: number; username: string }) => {
    console.log(`⏳ Player waiting for another round: ${data.username}`);

    // Show temporary notification
    setAnotherRoundNotification(`${data.username} is waiting for the next round`);
    setTimeout(() => {
      setAnotherRoundNotification(null);
    }, 3000);
  }, []);

  // Handle waiting room updates from server
  const handleWaitingRoomUpdate = useCallback((data: { usersInWaitingRoom: Array<{ userId: number; username: string }> }) => {
    console.log('🚪 Waiting room update:', data);
    setUsersInWaitingRoom(data.usersInWaitingRoom);
  }, []);

  useEffect(() => {
    if (!user) return;

    // Listen for opponent progress
    socketService.on('player_progress_update', handleOpponentProgress);
    socketService.on('player_completed', handlePlayerCompleted);
    socketService.on('game_ended', handleGameEnded);
    socketService.on('player_quit_game', handlePlayerQuit);
    socketService.on('another_round_waiting', handleAnotherRoundWaiting);
    socketService.on('waiting_room_update', handleWaitingRoomUpdate);

    return () => {
      socketService.off('player_progress_update', handleOpponentProgress);
      socketService.off('player_completed', handlePlayerCompleted);
      socketService.off('game_ended', handleGameEnded);
      socketService.off('player_quit_game', handlePlayerQuit);
      socketService.off('another_round_waiting', handleAnotherRoundWaiting);
      socketService.off('waiting_room_update', handleWaitingRoomUpdate);
    };
  }, [user, handleOpponentProgress, handlePlayerCompleted, handleGameEnded, handlePlayerQuit, handleAnotherRoundWaiting, handleWaitingRoomUpdate]);

  const handleMatchAttempt = (correct: boolean) => {
    if (!user) return;

    // Notify opponents of progress
    socketService.matchAttempt(room.id, user.id, correct);
  };

  const handleGameComplete = (stats: GameStats) => {
    if (!user || hasFinished) return;

    setHasFinished(true);

    // Submit result to server (Note: This is unused in current implementation)
    // Speed tracking is handled in App.tsx's handleCompetitiveGameComplete
    socketService.playerFinished(
      room.id,
      user.id,
      user.username,
      stats.completionTime,
      stats.totalAttempts,
      stats.correctMatches,
      stats.wrongMatches,
      5 // Default speed (actual speed tracking happens in App.tsx)
    );
  };

  const handleQuitGame = () => {
    if (!user) return;

    // Notify other players
    socketService.quitGame(room.id, user.id, user.username);

    // Leave the room
    socketService.leaveRoom(room.id, user.id);

    // Return to lobby
    onLeave();
  };

  // Show results screen
  if (gameResults) {
    return (
      <div className="competitive-results">
        <div className="results-header">
          <h2>🏆 Game Results - Round {roundNumber}</h2>
          <p className="room-info">
            {room.dictionaryName} - {room.pairCount} matches
            {room.matching_mode === 'sort' && (
              <span className="mode-badge sort-mode-badge">🏷️ Sort</span>
            )}
            {room.matching_mode === 'category' && (
              <span className="mode-badge category-mode-badge">📂 Category</span>
            )}
          </p>
          <div className="score-info-container">
            <button
              className="score-info-button"
              onClick={() => setShowScoreInfo(!showScoreInfo)}
              title="How is score calculated?"
            >
              ℹ️ Score Calculation
            </button>
            {showScoreInfo && (
              <div className="score-info-tooltip">
                <strong>Score Formula:</strong><br/>
                Score = (Accuracy × 50) - (Time × 10) - Speed Penalty + (Pairs × 300)<br/>
                Speed Penalty = (10 - speed)² × (Time / 5)<br/><br/>
                <strong>Strategy:</strong><br/>
                • More pairs = higher score potential<br/>
                • Higher speed = lower penalty<br/>
                • Balance speed, accuracy, and time!<br/><br/>
                <strong>Example (6 pairs, Speed 8, 30s, 95% acc):</strong><br/>
                (95 × 50) - (30 × 10) - 24 + (6 × 300) = 4,750 - 300 - 24 + 1,800 = 6,226
              </div>
            )}
          </div>
        </div>

        <h3 className="ranking-title">📊 Round {roundNumber} Results</h3>
        <div className="results-podium">
          {gameResults.slice(0, 3).map((result, index) => (
            <div key={result.user_id} className={`podium-place place-${index + 1}`}>
              <div className="place-number">
                {index === 0 && '🥇'}
                {index === 1 && '🥈'}
                {index === 2 && '🥉'}
              </div>
              <div className="player-name">{result.username}</div>
              <div className="player-stats">
                <div className="stat">
                  <span className="stat-label">Time:</span>
                  <span className="stat-value">{(result.completion_time / 1000).toFixed(2)}s</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Accuracy:</span>
                  <span className="stat-value">{result.accuracy.toFixed(1)}%</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Score:</span>
                  <span className="stat-value">{result.score}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {gameResults.length > 3 && (
          <div className="results-list">
            <h3>Round {roundNumber} - All Players</h3>
            {gameResults.map((result, index) => (
              <div key={result.user_id} className="result-row">
                <span className="placement">#{index + 1}</span>
                <span className="username">{result.username}</span>
                <span className="time">{(result.completion_time / 1000).toFixed(2)}s</span>
                <span className="accuracy">{result.accuracy.toFixed(1)}%</span>
                <span className="score">{result.score}</span>
              </div>
            ))}
          </div>
        )}

        {cumulativeResults && cumulativeResults.length > 0 && roundNumber > 1 && (
          <div className="cumulative-results">
            <h3 className="ranking-title">🏅 Total Cumulative Ranking</h3>
            <div className="results-list">
              {cumulativeResults.map((result) => (
                <div key={result.user_id} className="result-row cumulative">
                  <span className="placement">#{result.cumulative_placement}</span>
                  <span className="username">{result.username}</span>
                  <span className="rounds">Rounds: {result.rounds_played}</span>
                  <span className="total-score">Total Score: {result.total_score}</span>
                  <span className="cumulative-accuracy">
                    Accuracy: {((result.total_correct / result.total_attempts) * 100).toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Join Request Panel - Show on results page */}
        <JoinRequestPanel roomId={room.id} isInActiveGame={false} />

        {/* Persistent Waiting Room Status */}
        {usersInWaitingRoom.length > 0 && (
          <div className="waiting-room-status">
            <div className="waiting-room-header">
              🚪 Players in Waiting Room ({usersInWaitingRoom.length})
            </div>
            <div className="waiting-room-players">
              {usersInWaitingRoom.map(u => (
                <div key={u.userId} className="waiting-player">
                  {u.username} {u.userId === user?.id && '(You)'}
                </div>
              ))}
            </div>
            <div className="waiting-room-hint">
              {usersInWaitingRoom.some(u => u.userId === user?.id)
                ? 'Waiting for other players to join...'
                : 'Click "Start Another Round" to join them!'}
            </div>
          </div>
        )}

        <div className="results-actions">
          <button className="another-round-button" onClick={() => {
            if (user) {
              // Emit event to notify others
              socketService.getSocket()?.emit('start_another_round', {
                roomId: room.id,
                userId: user.id,
                username: user.username
              });
            }
            onAnotherRound();
          }}>
            Start Another Round
          </button>
          <button className="back-button" onClick={onLeave}>
            Leave Room
          </button>
        </div>
      </div>
    );
  }

  // If no results yet, don't render anything (game is being rendered in App.tsx)
  if (!gameResults) {
    return null;
  }

  // Render game with opponent tracking overlay
  return (
    <div className="competitive-game-container">
      {/* Quit Notification */}
      {quitNotification && (
        <div className="quit-notification">
          {quitNotification}
        </div>
      )}

      {/* Another Round Notification */}
      {anotherRoundNotification && (
        <div className="another-round-notification">
          {anotherRoundNotification}
        </div>
      )}

      {/* Opponent Progress Overlay */}
      <div className="opponents-overlay">
        <div className="opponents-header">
          <span className="room-code">Room: {room.roomCode}</span>
          <span className="dictionary-topic">{room.dictionaryName}</span>
          <div className="strategy-tip">⚡ Speed, accuracy & time affect your score!</div>
          <button className="quit-button" onClick={handleQuitGame}>
            Stop & Quit Room
          </button>
        </div>
        <div className="opponents-list">
          {Array.from(opponents.values()).map(opponent => (
            <div key={opponent.userId} className="opponent-card">
              <div className="opponent-info">
                <span className="opponent-name">{opponent.username}</span>
                {opponent.finished && <span className="finished-badge">✓ Finished</span>}
              </div>
              <div className="opponent-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(opponent.completed / opponent.total) * 100}%` }}
                  />
                </div>
                <span className="progress-text">
                  {opponent.completed}/{opponent.total}
                </span>
              </div>
              {/* Show real-time accuracy when player has made attempts */}
              {opponent.attempts > 0 && !opponent.finished && (
                <div className="opponent-accuracy">
                  Accuracy: {opponent.accuracy.toFixed(0)}%
                </div>
              )}
              {opponent.finished && opponent.completionTime && (
                <div className="opponent-time">
                  ⏱️ {(opponent.completionTime / 1000).toFixed(2)}s
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Render the actual game */}
      {renderGame(
        room.dictionaryData,
        room.pairCount,
        room.gameSeed,
        handleMatchAttempt,
        handleGameComplete,
        (room.matching_mode as 'one-to-one' | 'category' | 'sort') || 'one-to-one'
      )}
    </div>
  );
};

export default CompetitiveGame;
